import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { facilitiesAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  WifiIcon,
  TruckIcon,
  StarIcon,
  PencilIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface Facility {
  _id: string;
  name: string;
  licenseNumber: string;
  type: string;
  capacity: number;
  currentOccupancy: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  amenities: string[];
  costRange: string;
  minCost: number;
  maxCost: number;
  acceptsInsurance: boolean;
  specialties: string[];
  notes: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const FacilityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Fetch facility data
  const {
    data: facilityData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['facility', id],
    queryFn: () => facilitiesAPI.getFacility(id!),
    enabled: !!id
  });

  // Delete facility mutation
  const deleteFacilityMutation = useMutation({
    mutationFn: () => facilitiesAPI.deleteFacility(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
      queryClient.invalidateQueries({ queryKey: ['facilities-dashboard'] });
      showSuccess('Facility deleted successfully');
      navigate('/facilities');
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Failed to delete facility');
    }
  });

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load facility details. Please try again.</p>
        </div>
      </div>
    );
  }

  const facility = facilityData?.data?.data?.facility;

  if (!facility) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Facility not found.</p>
        </div>
      </div>
    );
  }

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      'Assisted Living': { color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'Nursing Home': { color: 'bg-red-100 text-red-800 border-red-200' },
      'Memory Care': { color: 'bg-purple-100 text-purple-800 border-purple-200' },
      'Independent Living': { color: 'bg-green-100 text-green-800 border-green-200' },
      'Skilled Nursing': { color: 'bg-orange-100 text-orange-800 border-orange-200' },
      'Rehabilitation Center': { color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
      'Adult Day Care': { color: 'bg-pink-100 text-pink-800 border-pink-200' },
      'Home Care': { color: 'bg-teal-100 text-teal-800 border-teal-200' },
      'Other': { color: 'bg-gray-100 text-gray-800 border-gray-200' }
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig['Other'];
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full border ${config.color}`}>
        {type}
      </span>
    );
  };

  const getAvailabilityStatus = () => {
    const available = facility.capacity - facility.currentOccupancy;
    if (available > 0) {
      return (
        <div className="flex items-center text-green-600">
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          <span className="font-medium">{available} beds available</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-red-600">
          <XCircleIcon className="w-5 h-5 mr-2" />
          <span className="font-medium">No availability</span>
        </div>
      );
    }
  };

  const getAmenityIcon = (amenity: string) => {
    const lowerAmenity = amenity.toLowerCase();
    if (lowerAmenity.includes('wifi') || lowerAmenity.includes('internet')) return WifiIcon;
    if (lowerAmenity.includes('transport') || lowerAmenity.includes('shuttle')) return TruckIcon;
    if (lowerAmenity.includes('medical') || lowerAmenity.includes('health')) return HeartIcon;
    return StarIcon;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this facility? This action cannot be undone.')) {
      deleteFacilityMutation.mutate();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/facilities')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Facilities
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{facility.name}</h1>
              <div className="flex items-center space-x-4">
                {getTypeBadge(facility.type)}
                <span className="text-sm text-gray-500">License: {facility.licenseNumber}</span>
                {facility.isActive ? (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></div>
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-1"></div>
                    Inactive
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3 mt-4 sm:mt-0">
              <Link
                to={`/facilities/${id}/edit`}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Facility
              </Link>
              
              <button
                onClick={handleDelete}
                disabled={deleteFacilityMutation.isPending}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {deleteFacilityMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="bg-white shadow-card rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <BuildingOfficeIcon className="w-6 h-6 text-blue-600 mr-3" />
                Overview
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <UserGroupIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{facility.capacity}</p>
                  <p className="text-sm text-gray-600">Total Capacity</p>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{facility.capacity - facility.currentOccupancy}</p>
                  <p className="text-sm text-gray-600">Available Beds</p>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <ClockIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{facility.currentOccupancy}</p>
                  <p className="text-sm text-gray-600">Current Occupancy</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                {getAvailabilityStatus()}
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${(facility.currentOccupancy / facility.capacity) * 100}%`
                    }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {Math.round((facility.currentOccupancy / facility.capacity) * 100)}% occupancy
                </p>
              </div>
            </div>

            {/* Location & Contact */}
            <div className="bg-white shadow-card rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPinIcon className="w-6 h-6 text-blue-600 mr-3" />
                Location & Contact
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Address</h3>
                  <p className="text-gray-600">
                    {facility.address.street}<br />
                    {facility.address.city}, {facility.address.state} {facility.address.zip}
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-600">
                      <PhoneIcon className="w-4 h-4 mr-3" />
                      <span>{facility.contact.phone}</span>
                    </div>
                    {facility.contact.email && (
                      <div className="flex items-center text-gray-600">
                        <EnvelopeIcon className="w-4 h-4 mr-3" />
                        <a href={`mailto:${facility.contact.email}`} className="text-blue-600 hover:text-blue-800">
                          {facility.contact.email}
                        </a>
                      </div>
                    )}
                    <p className="text-sm text-gray-500">Contact: {facility.contact.name}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {facility.amenities && facility.amenities.length > 0 && (
              <div className="bg-white shadow-card rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600 mr-3" />
                  Amenities
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {facility.amenities.map((amenity: string, index: number) => {
                    const IconComponent = getAmenityIcon(amenity);
                    return (
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <IconComponent className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Specialties */}
            {facility.specialties && facility.specialties.length > 0 && (
              <div className="bg-white shadow-card rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <HeartIcon className="w-6 h-6 text-blue-600 mr-3" />
                  Specialties
                </h2>
                
                <div className="flex flex-wrap gap-2">
                  {facility.specialties.map((specialty: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full border border-green-200"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {facility.notes && (
              <div className="bg-white shadow-card rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Notes</h2>
                <p className="text-gray-600 whitespace-pre-wrap">{facility.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Information */}
            <div className="bg-white shadow-card rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <CurrencyDollarIcon className="w-6 h-6 text-blue-600 mr-3" />
                Pricing
              </h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Cost Range</p>
                  <p className="text-lg font-semibold text-gray-900">{facility.costRange}</p>
                </div>
                
                {(facility.minCost > 0 || facility.maxCost > 0) && (
                  <div className="pt-4 border-t border-gray-200">
                    {facility.minCost > 0 && (
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-600">Minimum:</span>
                        <span className="text-sm font-medium">{formatPrice(facility.minCost)}</span>
                      </div>
                    )}
                    {facility.maxCost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Maximum:</span>
                        <span className="text-sm font-medium">{formatPrice(facility.maxCost)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center">
                    {facility.acceptsInsurance ? (
                      <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <XCircleIcon className="w-4 h-4 text-red-600 mr-2" />
                    )}
                    <span className="text-sm text-gray-700">
                      {facility.acceptsInsurance ? 'Accepts Insurance' : 'Does not accept insurance'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Facility Details */}
            <div className="bg-white shadow-card rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Details</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Created</p>
                  <p className="text-sm text-gray-600">
                    {new Date(facility.createdAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-700">Last Updated</p>
                  <p className="text-sm text-gray-600">
                    {new Date(facility.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-700">License Number</p>
                  <p className="text-sm text-gray-600">{facility.licenseNumber}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white shadow-card rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
              
              <div className="space-y-3">
                <Link
                  to={`/placements/new?facilityId=${facility._id}`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Create Placement
                </Link>
                
                <Link
                  to={`/placements?facilityId=${facility._id}`}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View Placements
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityDetail;
